import React, {useEffect, useRef} from 'react'
import {useForm} from 'react-hook-form'
import {
    Button,
    Box,
    Text,
    Flex,
    Link,
    Skeleton
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useMultiStyleConfig} from '@chakra-ui/react'
import {FormattedMessage, FormattedNumber, useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {useCustomerType} from '@salesforce/commerce-sdk-react'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useBrand} from '../../contexts/brand-context'
import WebFloatingSelect from '../web-floating-select'
import {CollectifLogoGarage, CollectifLogoDynamite} from '../icons'

const NONE_VALUE = 'none'

const LoyaltyCoupon = ({
    mode,
    isPointsApplied,
    isEligibleForLoyalty,
    isLoading,
    availablePoints,
    numberOfRedeemedPoints,
    cashDiscountAmount,
    currency,
    pointsEarned,
    reedemPoints,
    removePoints,
    previewContent,
    previewActionContent,
    previewActionHref
}) => {
    const styles = useMultiStyleConfig('LoyaltyCoupon', {})
    const {isRegistered} = useCustomerType()
    const navigate = useNavigation()
    const {isGarage} = useBrand()
    const intl = useIntl()

    const CollectifLogo = isGarage ? CollectifLogoGarage : CollectifLogoDynamite
    const collectifLogoLabel = isGarage
        ? intl.formatMessage({defaultMessage: 'GARAGE COLLECTIF', id: 'loyalty_coupon.title'})
        : intl.formatMessage({
              defaultMessage: 'DYNAMITE COLLECTIF',
              id: 'loyalty_coupon.title_dynamite.dynamite'
          })
    const collectifLogo = (
        <CollectifLogo
            width={isGarage ? {base: '134.6px', lg: '157px'} : '188.25px'}
            height={isGarage ? {base: '18px', lg: '20px'} : '12px'}
            role="img"
            aria-label={collectifLogoLabel}
            display="block"
        />
    )

    if (mode === 'preview') {
        const hasCustomPreviewAction = Boolean(previewActionHref)

        return (
            <Box {...styles.container}>
                <Box {...styles.title}>{collectifLogo}</Box>

                <Flex align="center" justify="space-between">
                    <Text {...styles.footer}>
                        {previewContent || (
                            <FormattedMessage
                                id="loyalty_coupon.points_earned"
                                defaultMessage="You'll earn <b>{points} points</b> with this purchase."
                                values={{
                                    points: (
                                        <Text as="span" data-private>
                                            {pointsEarned}
                                        </Text>
                                    ),
                                    b: (chunks) => (
                                        <Text {...styles.footerPoints}>
                                            {chunks}
                                        </Text>
                                    )
                                }}
                            />
                        )}
                    </Text>

                    {hasCustomPreviewAction ? (
                        <Link href={previewActionHref} {...styles.removeLink} data-testid="loyalty-coupon-link-preview-action">
                            {previewActionContent || (
                                <FormattedMessage
                                    id="checkout_confirmation.button.see_rewards"
                                    defaultMessage="See my rewards"
                                />
                            )}
                        </Link>
                    ) : (
                        !isRegistered && (
                            <Link
                                as="button"
                                onClick={() => navigate('/login')}
                                {...styles.removeLink}
                                data-testid="loyalty-coupon-link-register"
                            >
                                <FormattedMessage id="auth_register.tab" defaultMessage="Register" />
                            </Link>
                        )
                    )}
                </Flex>
            </Box>
        )
    }

    const {control, watch, setValue} = useForm({
        defaultValues: {pointsSelection: NONE_VALUE}
    })

    const previousAvailablePointsRef = useRef(availablePoints)
    const previousNumberOfRedeemedPointsRef = useRef(numberOfRedeemedPoints)
    const previousCashDiscountAmountRef = useRef(cashDiscountAmount)
    const previousIsEligibleForLoyaltyRef = useRef(isEligibleForLoyalty)

    // Track previous values
    useEffect(() => {
        if (availablePoints != null) {
            previousAvailablePointsRef.current = availablePoints
        }
    }, [availablePoints])

    useEffect(() => {
        if (numberOfRedeemedPoints != null) {
            previousNumberOfRedeemedPointsRef.current = numberOfRedeemedPoints
        }
    }, [numberOfRedeemedPoints])

    useEffect(() => {
        if (cashDiscountAmount != null) {
            previousCashDiscountAmountRef.current = cashDiscountAmount
        }
    }, [cashDiscountAmount])

    useEffect(() => {
        if (isEligibleForLoyalty) {
            previousIsEligibleForLoyaltyRef.current = isEligibleForLoyalty
        }
    }, [isEligibleForLoyalty])

    const selectedValue = watch('pointsSelection')
    const isNoneSelected = selectedValue === NONE_VALUE

    // Reset dropdown to "None" when points are removed
    useEffect(() => {
        if (!isPointsApplied) {
            setValue('pointsSelection', NONE_VALUE)
        }
    }, [isPointsApplied])

    const handleApply = () => {
        if (!isNoneSelected) {
            reedemPoints()
        }
    }

    const resolvedRedeemedPoints =
        numberOfRedeemedPoints ?? previousNumberOfRedeemedPointsRef.current ?? 0
    const resolvedCashDiscountAmount =
        cashDiscountAmount ?? previousCashDiscountAmountRef.current

    const discountLabel = resolvedCashDiscountAmount != null && currency
        ? `${resolvedRedeemedPoints} points = `
        : `${resolvedRedeemedPoints} pts`

    // Show skeleton while loading
    if (isLoading && isRegistered) {
        return (
            <Box {...styles.container}>
                {/* Skeleton title */}
                <Skeleton height="24px" width="150px" mb={2} borderRadius="md" />

                {/* Skeleton subtitle */}
                <Skeleton height="18px" width="100%" mb={3} borderRadius="md" />

                {/* Skeleton dropdown and button */}
                <Box display="flex" gap={2} mt={3}>
                    <Skeleton height="40px" flex={1} borderRadius="md" />
                    <Skeleton height="40px" width="80px" borderRadius="md" />
                </Box>
            </Box>
        )
    }

    return (
        <Box {...styles.container}>
            {/* Title */}
            <Box {...styles.title}>{collectifLogo}</Box>

            {/* Subtitle — points balance for logged-in users (shown even with 0 points) */}
            {isRegistered && (
                <Text {...styles.subtitle}>
                    <FormattedMessage
                        defaultMessage="You currently have <b>{points} points</b>"
                        id="loyalty_coupon.points_balance"
                        values={{
                            points: (
                                <Text as="span" data-private>
                                    {(previousAvailablePointsRef.current ?? 0).toLocaleString()}
                                </Text>
                            ),
                            b: (chunks) => (
                                <Text as="span" textStyle={'bodyXsBold'}>
                                    {chunks}
                                </Text>
                            )
                        }}
                    />
                </Text>
            )}

            {/* Dropdown + Apply (only for eligible logged-in users, and not yet applied) */}
            {isRegistered && (isEligibleForLoyalty || previousIsEligibleForLoyaltyRef.current) && !isPointsApplied && (
                <Box {...styles.selectWrapper}>
                    <WebFloatingSelect
                        name="pointsSelection"
                        label={
                            <FormattedMessage
                                defaultMessage="Exchange Points"
                                id="loyalty_coupon.dropdown_label"
                            />
                        }
                        secondaryLabel={
                            <FormattedMessage
                                defaultMessage="(optional)"
                                id="loyalty_coupon.dropdown_optional"
                            />
                        }
                        options={[
                            {
                                label: (
                                    <FormattedMessage
                                        defaultMessage="None"
                                        id="loyalty_coupon.option_none"
                                    />
                                ),
                                value: NONE_VALUE
                            },
                            {
                                label: (resolvedCashDiscountAmount != null && currency) ? (
                                    <FormattedMessage
                                        defaultMessage="{points} points = {discount}"
                                        id="loyalty_coupon.option_redeem"
                                        values={{
                                            points: (
                                                <Text as="span" data-private>
                                                    {resolvedRedeemedPoints}
                                                </Text>
                                            ),
                                            discount: (
                                                <Text as="span" data-private>
                                                    <FormattedNumber
                                                        value={resolvedCashDiscountAmount}
                                                        style="currency"
                                                        currency={currency}
                                                    />
                                                </Text>
                                            )
                                        }}
                                    />
                                ) : (
                                    `${resolvedRedeemedPoints} pts`
                                ),
                                value: 'redeem'
                            }
                        ]}
                        control={control}
                        defaultValue={NONE_VALUE}
                        showSelectedCheckmark
                    />
                    <Button
                        variant="webSecondaryOutlineTransparent"
                        onClick={handleApply}
                        isDisabled={isNoneSelected}
                        isLoading={isLoading}
                        data-testid="loyalty-coupon-button-apply"
                        {...styles.applyCTA}
                    >
                        <FormattedMessage
                            defaultMessage="Apply"
                            id="loyalty_coupon.button_apply"
                        />
                    </Button>
                </Box>
            )}

            {/* Applied state — discount row + Remove link */}
            {isRegistered && (isEligibleForLoyalty || previousIsEligibleForLoyaltyRef.current) && isPointsApplied && (
                <Box>
                    <Flex {...styles.appliedRow}>
                        <Text {...styles.appliedText} data-private>
                            {discountLabel}
                            {resolvedCashDiscountAmount != null && currency && (
                                <FormattedNumber
                                    value={resolvedCashDiscountAmount}
                                    style="currency"
                                    currency={currency}
                                />
                            )}
                        </Text>
                        <Button
                            variant="unstyled"
                            onClick={removePoints}
                            isLoading={isLoading}
                            {...styles.removeLink}
                            data-testid="loyalty-coupon-button-remove"
                        >
                            <FormattedMessage
                                defaultMessage="Remove"
                                id="loyalty_coupon.button_remove"
                            />
                        </Button>
                    </Flex>
                </Box>
            )}

            {/* Footer — points to earn */}
            {Number(pointsEarned) > 0 && (
                <Flex align="center" justify="space-between">
                    <Text {...styles.footer}>
                        <FormattedMessage
                            defaultMessage="You'll earn <b>{points} points</b> with this purchase."
                            id="loyalty_coupon.points_earned"
                            values={{
                                points: (
                                    <Text as="span" data-private>
                                        {pointsEarned}
                                    </Text>
                                ),
                                b: (chunks) => (
                                    <Text {...styles.footerPoints}>
                                        {chunks}
                                    </Text>
                                )
                            }}
                        />
                    </Text>
                    {!isRegistered && (
                        <Link
                            as="button"
                            onClick={() => navigate('/login')}
                            {...styles.removeLink}
                            data-testid="loyalty-coupon-link-register"
                        >
                            <FormattedMessage
                                defaultMessage="Register"
                                id="loyalty_coupon.register_link"
                            />
                        </Link>
                    )}
                </Flex>
            )}
        </Box>
    )
}

LoyaltyCoupon.propTypes = {
    mode: PropTypes.oneOf(['default', 'preview']),
    isPointsApplied: PropTypes.bool,
    isEligibleForLoyalty: PropTypes.bool,
    isLoading: PropTypes.bool,
    availablePoints: PropTypes.number,
    numberOfRedeemedPoints: PropTypes.number,
    cashDiscountAmount: PropTypes.number,
    currency: PropTypes.string,
    pointsEarned: PropTypes.number,
    reedemPoints: PropTypes.func,
    removePoints: PropTypes.func,
    previewContent: PropTypes.node,
    previewActionContent: PropTypes.node,
    previewActionHref: PropTypes.string
}

export default LoyaltyCoupon
